import { TDateISO } from "~/@types/TDateISO";
import sfAuth from "~/auth/SFAuth";
import Config from "~/config";
import { api as generatedApi } from "~/gql/generatedApi.generated";
import unauthorizedRetryPolicy from "~/helpers/unauthorizedRetryPolicy";
import authorizationHeader from "~/services/authorizationHeader";

export interface OnboardingUploadResult {
	blobName: string;
	fileName: string;
	occurredAt: TDateISO;
	sizeBytes: number;
	sourceId: string;
	sourceVersion: number;
	uploadedBy: string;
}

const UNAUTHORIZED_STATUS = 401;

const readErrorBody = async (response: Response): Promise<unknown> => {
	try {
		return await response.json();
	} catch {
		return undefined;
	}
};

const onboardingUploadApi = generatedApi.injectEndpoints({
	endpoints: (build) => ({
		UploadOnboardingFile: build.mutation<OnboardingUploadResult, File>({
			queryFn: async (file, api) => {
				const formData = new FormData();
				formData.append("file", file);

				try {
					const uploadUrl = `${Config.endpoint.replace(/\/graphql\/?$/, "")}/onboarding/upload`;
					// FormData is re-sendable, so a replay reuses this same instance.
					const send = (token: string) =>
						fetch(uploadUrl, {
							body: formData,
							headers: { Authorization: authorizationHeader(token) },
							method: "POST"
						});

					const token = await sfAuth.checkSession();
					// The base queries skip the request entirely rather than send an empty Bearer.
					if (!token) {
						return { error: { data: undefined, status: UNAUTHORIZED_STATUS } };
					}

					let response = await send(token);

					if (response.status === UNAUTHORIZED_STATUS) {
						const refreshedToken = await sfAuth.checkSession(true);
						const resolution = await unauthorizedRetryPolicy.resolveUnauthorized(
							{ operationType: api.type, tokenRefreshed: !!refreshedToken },
							{
								redirectToLogin: () => sfAuth.redirectToLogin(),
								// async so the MaybePromise the transport returns is settled here, not by the helper.
								replay: async () => send(refreshedToken)
							}
						);

						if (resolution.action === "redirectToLogin") {
							return { error: { data: await readErrorBody(response), status: response.status } };
						}

						if (resolution.action === "surfaceNotReplayed") {
							return { error: resolution.notReplayedError };
						}

						response = resolution.replayed;
					}

					if (!response.ok) {
						return { error: { data: await readErrorBody(response), status: response.status } };
					}

					return { data: (await response.json()) as OnboardingUploadResult };
				} catch (error) {
					return { error: { error: error instanceof Error ? error.message : String(error), status: "FETCH_ERROR" } };
				}
			}
		})
	})
});

export const { useUploadOnboardingFileMutation } = onboardingUploadApi;

export default onboardingUploadApi;
